import type * as React from 'react';
import { useMemo, useState } from 'react';
import { FileTextIcon, PlusIcon, TableIcon, Trash2Icon } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '../../../components/ui/button';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '../../../components/ui/empty';
import { Field, FieldDescription, FieldLabel } from '../../../components/ui/field';
import { Input } from '../../../components/ui/input';
import { Spinner } from '../../../components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Textarea } from '../../../components/ui/textarea';
import { ExportMenu, FORMAT_DOCX, FORMAT_GDOC } from '../../statistics/components/ExportMenu';
import { GoogleDocsLinks } from '../../statistics/components/GoogleDocsLinks';
import {
  downloadDocx,
  openDocxInGoogleDocs,
  requestGoogleDocsToken,
} from '../../statistics/lib/open-docx-in-gdocs';
import { extractShortcode } from '../../statistics/lib/parse-instagram-url';
import { extractLinks } from '../lib/extract-links';
import type { ScreenImage } from '../lib/render/insights-canvas';
import { longDate, patchFromScreen, spacedNumber, type PostRow } from '../lib/post-rows';
import {
  DEFAULT_SERVICE,
  buildActDocument,
  type ActDocumentRow,
  type ActScreen,
} from '../lib/service-act/document';
import { usePostRows } from '../model/usePostRows';
import { CellInput } from './cell-input';
import { ScreenCell } from './screen-cell';
import { ImagePasteProvider } from './image-field';

/**
 * Отчёт об оказанных услугах — таблица размещений и Word по форме клиента.
 *
 * Собирается по тем же ссылкам, что и презентация: медиаресурс, дату,
 * формат, просмотры и охваты отдаёт InstaFarm. Руками остаются деньги —
 * их API не знает, — и строки по публикациям, до которых мы не дотянулись.
 * Поэтому таблица целиком правится здесь: то, что собрано, лишь заготовка.
 */

const DATE_HINT = '14.09.2026';

/** Картинка для Word: байты те же, размер уже известен из рендера. */
async function toActScreen(screen: ScreenImage | null): Promise<ActScreen | null> {
  if (!screen) return null;
  return {
    data: new Uint8Array(await screen.blob.arrayBuffer()),
    width: screen.width,
    height: screen.height,
    type: screen.blob.type === 'image/png' ? 'png' : 'jpg',
  };
}

export function ServiceActReport(): React.ReactElement {
  const act = usePostRows();
  const [linksText, setLinksText] = useState('');
  const [service, setService] = useState(DEFAULT_SERVICE);
  const [saving, setSaving] = useState<'docx' | 'gdoc' | null>(null);
  const [docUrls, setDocUrls] = useState<string[]>([]);

  const links = useMemo(() => extractLinks(linksText), [linksText]);
  const valid = useMemo(() => links.filter((url) => extractShortcode(url)).length, [links]);

  const { rows, totals, from, to, isBusy, progress } = act;
  const missing = rows.filter((row) => row.manual).length;
  const withoutScreen = rows.filter((row) => !row.screen).length;

  async function handleSave(target: string): Promise<void> {
    setSaving(target === 'gdoc' ? 'gdoc' : 'docx');
    setDocUrls([]);
    try {
      // Вход в Google — первым делом, пока клик ещё свежий.
      const token = target === 'gdoc' ? await requestGoogleDocsToken() : null;
      const screens = await Promise.all(rows.map((row) => toActScreen(row.screen)));
      const documentRows: ActDocumentRow[] = rows.map((row, i) => ({
        service,
        resource: row.resource,
        date: row.date,
        url: row.url,
        format: row.format,
        views: row.views,
        reach: row.reach,
        cost: row.cost,
        screen: screens[i],
      }));
      const { Packer } = await import('docx');
      const file = await Packer.toBlob(
        buildActDocument({
          from: from ? longDate(from) : '________',
          to: to ? longDate(to) : '________',
          rows: documentRows,
          // Итог по строкам: цены стоят в таблице, и второй суммы быть не может.
          totals: {
            views: spacedNumber(totals.views),
            reach: spacedNumber(totals.reach),
            cost: totals.cost > 0 ? spacedNumber(totals.cost) : '__________',
          },
        }),
      );
      const docx = { blob: file, filename: `Отчет по услугам ${to || from || ''}`.trim() + '.docx' };
      if (token) setDocUrls(await openDocxInGoogleDocs(token, [docx]));
      else downloadDocx(docx);
      toast.success('Отчёт собран');
    } catch (err) {
      console.error('[Отчёт] Word не собрался:', err);
      toast.error(err instanceof Error ? err.message : 'Не удалось собрать отчёт');
    } finally {
      setSaving(null);
    }
  }

  return (
    <ImagePasteProvider>
      <div className="space-y-6">
        <Field>
          <FieldLabel htmlFor="act-links">Ссылки на публикации</FieldLabel>
          <Textarea
            id="act-links"
            rows={6}
            placeholder={'https://www.instagram.com/p/…\nhttps://www.instagram.com/reel/…'}
            value={linksText}
            disabled={isBusy}
            onChange={(e) => setLinksText(e.target.value)}
          />
          <FieldDescription>
            В том порядке, в каком нужны строки. Подписи между ссылками можно не убирать.
            {links.length > 0 ? ` Распознано: ${valid} из ${links.length}.` : ''}
          </FieldDescription>
        </Field>

        <div className="flex flex-wrap items-center gap-2">
          <Button disabled={isBusy || links.length === 0} onClick={() => void act.collect(linksText)}>
            {isBusy ? <Spinner /> : <TableIcon />}
            Собрать таблицу
          </Button>
          <Button variant="outline" disabled={isBusy} onClick={act.addRow}>
            <PlusIcon />
            Добавить строку
          </Button>
          {progress ? <p className="text-muted-foreground text-sm">{progress}</p> : null}
        </div>

        {rows.length === 0 ? (
          <Empty className="border-dashed">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <FileTextIcon />
              </EmptyMedia>
              <EmptyTitle>Таблицы пока нет</EmptyTitle>
              <EmptyDescription>
                Вставьте ссылки и нажмите «Собрать таблицу»: медиаресурс, дату, формат, просмотры
                и охваты подставим сами. Цены и строки по чужим пабликам заполняются руками.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <>
            <div className="space-y-3">
              <Table className="min-w-4xl">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">№</TableHead>
                    <TableHead className="w-40">Медиаресурс</TableHead>
                    <TableHead className="w-28">Дата</TableHead>
                    <TableHead className="min-w-48">Ссылка</TableHead>
                    <TableHead className="w-28">Формат</TableHead>
                    <TableHead className="w-24 text-right">Просмотры</TableHead>
                    <TableHead className="w-24 text-right">Охваты</TableHead>
                    <TableHead className="w-24 text-right">Стоимость</TableHead>
                    <TableHead className="w-16" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row, i) => (
                    <ActTableRow
                      key={row.key}
                      row={row}
                      index={i}
                      disabled={isBusy}
                      onPatch={(patch) => act.patchRow(row.key, patch)}
                      onRemove={() => act.removeRow(row.key)}
                    />
                  ))}
                </TableBody>
              </Table>

              <p className="text-muted-foreground text-sm">
                Итого: просмотров{' '}
                <span className="text-foreground font-medium tabular-nums">
                  {spacedNumber(totals.views)}
                </span>
                , охватов{' '}
                <span className="text-foreground font-medium tabular-nums">
                  {spacedNumber(totals.reach)}
                </span>
                , стоимость{' '}
                <span className="text-foreground font-medium tabular-nums">
                  {totals.cost > 0 ? `${spacedNumber(totals.cost)} ₸` : 'не заполнена'}
                </span>
                .{missing > 0 ? ` Без цифр строк: ${missing}.` : ''}
                {withoutScreen > 0 ? ` Без скриншота: ${withoutScreen}.` : ''}
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field>
                <FieldLabel htmlFor="act-from">Отчётный период с</FieldLabel>
                <Input
                  id="act-from"
                  value={from}
                  placeholder={DATE_HINT}
                  disabled={isBusy}
                  onChange={(e) => act.setFrom(e.target.value)}
                />
                <FieldDescription>
                  Подставлена первая дата размещения — в договоре период обычно шире.
                </FieldDescription>
              </Field>
              <Field>
                <FieldLabel htmlFor="act-to">по</FieldLabel>
                <Input
                  id="act-to"
                  value={to}
                  placeholder={DATE_HINT}
                  disabled={isBusy}
                  onChange={(e) => act.setTo(e.target.value)}
                />
                <FieldDescription>Последняя дата размещения.</FieldDescription>
              </Field>
            </div>

            <Field>
              <FieldLabel htmlFor="act-service">Наименование услуги</FieldLabel>
              <Input
                id="act-service"
                value={service}
                disabled={isBusy}
                onChange={(e) => setService(e.target.value)}
              />
              <FieldDescription>Встанет в каждую строку таблицы, как в форме клиента.</FieldDescription>
            </Field>

            <ExportMenu
              rememberKey="report:act"
              label="Выгрузить"
              formats={[FORMAT_GDOC, FORMAT_DOCX]}
              onSelect={handleSave}
              disabled={isBusy}
              busy={saving !== null}
              busyLabel="Собираем отчёт…"
              className="w-full"
            />
            {saving === null && <GoogleDocsLinks urls={docUrls} />}
          </>
        )}
      </div>
    </ImagePasteProvider>
  );
}

function ActTableRow({
  row,
  index,
  disabled,
  onPatch,
  onRemove,
}: {
  row: PostRow;
  index: number;
  disabled: boolean;
  onPatch: (patch: Partial<PostRow>) => void;
  onRemove: () => void;
}): React.ReactElement {
  return (
    <TableRow>
      <TableCell className="text-muted-foreground tabular-nums">{index + 1}</TableCell>
      <TableCell>
        <CellInput
          label={`Медиаресурс, строка ${index + 1}`}
          value={row.resource}
          disabled={disabled}
          onChange={(resource) => onPatch({ resource })}
        />
      </TableCell>
      <TableCell>
        <CellInput
          label={`Дата, строка ${index + 1}`}
          value={row.date}
          disabled={disabled}
          onChange={(date) => onPatch({ date })}
        />
      </TableCell>
      <TableCell>
        <CellInput
          label={`Ссылка, строка ${index + 1}`}
          value={row.url}
          disabled={disabled}
          onChange={(url) => onPatch({ url })}
        />
      </TableCell>
      <TableCell>
        <CellInput
          label={`Формат, строка ${index + 1}`}
          value={row.format}
          disabled={disabled}
          onChange={(format) => onPatch({ format })}
        />
      </TableCell>
      <TableCell>
        <CellInput
          label={`Просмотры, строка ${index + 1}`}
          value={row.views}
          align="right"
          disabled={disabled}
          onChange={(views) => onPatch({ views })}
        />
      </TableCell>
      <TableCell>
        <CellInput
          label={`Охваты, строка ${index + 1}`}
          value={row.reach}
          align="right"
          disabled={disabled}
          onChange={(reach) => onPatch({ reach })}
        />
      </TableCell>
      <TableCell>
        <CellInput
          label={`Стоимость, строка ${index + 1}`}
          value={row.cost}
          align="right"
          disabled={disabled}
          onChange={(cost) => onPatch({ cost })}
        />
      </TableCell>
      <TableCell>
        <div className="flex items-center justify-end gap-0.5">
          <ScreenCell
            screen={row.screen}
            title={row.resource || row.url}
            fieldId={row.key}
            disabled={disabled}
            onChange={(screen) => onPatch({ screen })}
            onRecognized={(data) => onPatch(patchFromScreen(row, data))}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={`Убрать строку ${index + 1}`}
            disabled={disabled}
            onClick={onRemove}
          >
            <Trash2Icon className="text-muted-foreground" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
